import React, { useMemo, useState } from 'react';
import { Ejecutiva, FilaImportar, FilaRechazada } from '../../types/Altas';
import { Badge, Button, Modal } from '../ui';
import { SpinnerIcon } from '../icons';
import { inputClass, labelClass } from './altasUi';
import { parsearPegado } from './importarHelpers';

interface Props {
  ejecutivas: Ejecutiva[];
  planes: string[];
  onImportar: (
    ejecutivaId: number,
    filas: FilaImportar[],
  ) => Promise<{ creadas: number; rechazadas: FilaRechazada[] } | { error: string }>;
  onClose: () => void;
}

const th = 'pb-1.5 pe-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400';

/**
 * Carga masiva pegando filas desde el Excel del jefe comercial: una pestaña
 * por ejecutiva, así que se elige la ejecutiva y se pega esa pestaña. La
 * vista previa muestra qué se entendió de cada fila antes de confirmar.
 */
const ImportarModal: React.FC<Props> = ({ ejecutivas, planes, onImportar, onClose }) => {
  const activas = ejecutivas.filter((e) => e.activa);
  // Sin ejecutiva preseleccionada a propósito: con una por defecto, la segunda
  // pestaña que se pega queda cargada a la ejecutiva de la primera sin que nadie lo note.
  const [ejecutivaId, setEjecutivaId] = useState('');
  const [texto, setTexto] = useState('');
  const [importando, setImportando] = useState(false);
  const [resultado, setResultado] = useState<{
    creadas: number;
    rechazadas: FilaRechazada[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filas = useMemo(() => parsearPegado(texto, planes), [texto, planes]);
  const ejecutiva = activas.find((e) => String(e.id) === ejecutivaId) ?? null;
  const listas = filas.filter((f) => f.error === null);
  const conError = filas.length - listas.length;

  const importar = async () => {
    if (listas.length === 0 || ejecutivaId === '') return;
    setImportando(true);
    let r: Awaited<ReturnType<typeof onImportar>>;
    try {
      r = await onImportar(
        Number(ejecutivaId),
        listas.map((f) => ({
          dni: f.dni as string,
          nombre: f.nombre ?? undefined,
          plan: f.plan as string,
        })),
      );
    } catch (err) {
      r = { error: err instanceof Error ? err.message : 'No se pudieron importar las altas' };
    } finally {
      setImportando(false);
    }
    if ('error' in r) {
      setError(r.error);
      return;
    }
    // El backend numera las filas que recibió; acá se traducen a la fila del pegado,
    // que es la que la persona ve en la vista previa y en su Excel.
    setResultado({
      ...r,
      rechazadas: r.rechazadas.map((x) => ({ ...x, fila: listas[x.fila - 1]?.fila ?? x.fila })),
    });
  };

  if (resultado) {
    return (
      <Modal titulo="Importación terminada" onClose={onClose} size="lg">
        <p className="text-sm text-gray-600">
          Se cargaron <span className="font-semibold">{resultado.creadas}</span>{' '}
          {resultado.creadas === 1 ? 'alta' : 'altas'}.
          {resultado.rechazadas.length > 0 && (
            <>
              {' '}
              Quedaron afuera {resultado.rechazadas.length}; corregilas en el Excel o cargalas a
              mano:
            </>
          )}
        </p>
        {resultado.rechazadas.length > 0 && (
          <ul className="mt-3 max-h-64 space-y-1 overflow-y-auto text-xs text-slate-600">
            {resultado.rechazadas.map((r) => (
              <li key={`${r.fila}-${r.dni}`}>
                <span className="tabular-nums text-slate-400">Fila {r.fila}</span>
                {r.dni && <span className="ms-2 tabular-nums">{r.dni}</span>}
                <span className="ms-2">{r.motivo}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="mt-5 flex justify-end">
          <Button type="button" variant="primary" onClick={onClose}>
            Listo
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal titulo="Pegar desde Excel" onClose={onClose} size="lg">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Elegí el ejecutivo, copiá las filas de su pestaña del Excel (nombre, DNI y plan, con o sin
          encabezado) y pegalas acá. Antes de confirmar vas a ver qué se entendió de cada fila.
        </p>

        <div>
          <label htmlFor="importar-ejecutiva" className={labelClass}>
            Ejecutivo
          </label>
          <select
            id="importar-ejecutiva"
            value={ejecutivaId}
            onChange={(e) => setEjecutivaId(e.target.value)}
            className={inputClass}
            disabled={activas.length === 0}
          >
            <option value="">
              {activas.length === 0 ? 'Cargá un ejecutivo primero' : 'Elegí el ejecutivo'}
            </option>
            {activas.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nombre}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="importar-texto" className={labelClass}>
            Filas pegadas
          </label>
          <textarea
            id="importar-texto"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            rows={6}
            placeholder={'Apellido, Nombre\t12345678\tPlan 7000'}
            className={`${inputClass} font-mono text-xs`}
          />
        </div>

        {filas.length > 0 && (
          <div>
            <p className="mb-2 text-xs text-slate-500">
              {listas.length} {listas.length === 1 ? 'fila lista' : 'filas listas'} para importar
              {conError > 0 && <> · {conError} con problemas, que se saltean</>}
            </p>
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200 text-left">
                    <th className={th}>#</th>
                    <th className={th}>DNI</th>
                    <th className={th}>Nombre</th>
                    <th className={th}>Plan</th>
                    <th className={th}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {filas.map((f) => (
                    <tr key={f.fila} className="border-b border-slate-100 last:border-0">
                      <td className="py-1 pe-3 tabular-nums text-slate-400">{f.fila}</td>
                      <td className="py-1 pe-3 tabular-nums">{f.dni ?? '—'}</td>
                      <td className="py-1 pe-3">{f.nombre ?? '—'}</td>
                      <td className="py-1 pe-3">{f.plan ?? f.planTexto ?? '—'}</td>
                      <td className="py-1">
                        {f.error ? (
                          <Badge tone="warning" title={f.error}>
                            {f.error}
                          </Badge>
                        ) : (
                          <Badge tone="positive">Lista</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {error && (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700" role="alert">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={importar}
            disabled={listas.length === 0 || ejecutiva === null || importando}
            icon={importando ? <SpinnerIcon /> : undefined}
          >
            {ejecutiva && listas.length > 0
              ? `Importar ${listas.length} para ${ejecutiva.nombre}`
              : 'Importar'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ImportarModal;
